import { ContactDetails } from '@futrli-sdk/predict';

import { ApiView, ApiViewState, getDefaultApiViewState } from './core/ApiView';
import { contactsQuery, contactsQueryKey, contactDetailQuery, contactDetailQueryKey } from './queries/contacts';
import { fetchCustomersAndSuppliers, fetchCustomersAndSuppliersDetails } from '../predict/src/core/api/customers-and-suppliers';
import { transformContactData } from '../predict/src/dashboard/components/customers-and-suppliers-tab/context/helpers';
import { TransformedContact } from '../predict/src/dashboard/components/customers-and-suppliers-tab/context/types';

export interface CustomersAndSuppliersViewState extends ApiViewState {
  activeContact?: ContactDetails;
  contacts: TransformedContact[];
  isContactDetailLoading: boolean;
  isContactsLoading: boolean;
  isSidePanelOpen: boolean;
}

export class CustomersAndSuppliersView extends ApiView {
  private contactsObs: ReturnType<typeof contactsQuery>;
  private contactDetailObs: ReturnType<typeof contactDetailQuery>;

  private _period: string;

  public state: CustomersAndSuppliersViewState = {
    ...getDefaultApiViewState(),
    activeContact: undefined,
    contacts: [],
    isContactDetailLoading: false,
    isContactsLoading: true,
    isSidePanelOpen: false
  };

  constructor(
    private workspaceId: string,
    private organisationId: string,
    initialPeriod: string
  ) {
    super();

    this._period = initialPeriod;

    // Queries
    this.contactsObs = this.addQuery(contactsQuery(workspaceId, organisationId, initialPeriod));
    this.contactDetailObs = this.addQuery(contactDetailQuery(workspaceId, organisationId, '', ''));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public closeSidePanel() {
    this.clearActiveContact();
  }

  public fetchContactDetail(contactId: string, contactType: string) {
    const currentData = this.getResult(this.contactDetailObs).data;
    if (contactId === currentData?.id) {
      return;
    }

    this.contactDetailObs.setOptions({
      queryKey: contactDetailQueryKey(this.organisationId, this.workspaceId, contactId),
      queryFn: () => fetchCustomersAndSuppliersDetails(this.workspaceId, this.organisationId, contactId, { type: contactType }),
      enabled: true
    });
  }

  public setContactsPeriod(period: string) {
    if (this._period === period) {
      return;
    }
    this._period = period;

    this.contactsObs.setOptions({
      queryKey: contactsQueryKey(this.organisationId, this.workspaceId, period),
      queryFn: () => fetchCustomersAndSuppliers(this.workspaceId, this.organisationId, { period }),
      enabled: !!period
    });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private clearActiveContact() {
    this.contactDetailObs.setOptions({
      queryKey: contactDetailQueryKey(this.organisationId, this.workspaceId, ''),
      queryFn: () => fetchCustomersAndSuppliersDetails(this.workspaceId, this.organisationId, '', { type: '' }),
      enabled: false
    });
  }

  private get activeContact(): ContactDetails | undefined {
    const result = this.getResult(this.contactDetailObs);
    if (!result.data || result.isLoading) {
      return undefined;
    }
    return result.data;
  }

  private get contacts(): TransformedContact[] {
    const raw = this.getResult(this.contactsObs).data;
    if (!raw) {
      return [];
    }
    return transformContactData(raw);
  }

  private get isContactDetailLoading(): boolean {
    return this.getResult(this.contactDetailObs).isLoading;
  }

  private get isContactsLoading(): boolean {
    return this.getResult(this.contactsObs).isLoading;
  }

  protected getNewState(): CustomersAndSuppliersViewState {
    return {
      ...super.getNewState(),
      activeContact: this.activeContact,
      contacts: this.contacts,
      isContactDetailLoading: this.isContactDetailLoading,
      isContactsLoading: this.isContactsLoading,
      isSidePanelOpen: this.activeContact !== undefined || this.isContactDetailLoading
    };
  }
}
